import { buildOrganizationJsonLd, buildSoftwareApplicationJsonLd, buildWebsiteJsonLd } from "@reactive-resume/utils";
import { useLayoutEffect } from "react";
import { Helmet } from "react-helmet-async";

import { BRANDING } from "@/client/constants/branding";

type Props = {
  title: string;
  description?: string;
  /** Path-only canonical, e.g. `/pricing`. The component prefixes BRANDING.url. */
  canonical?: string;
  noindex?: boolean;
  lang?: string;
  /**
   * Optional JSON-LD blocks to emit in addition to the global Organization
   * + WebSite blocks. Pass any number of structured-data objects (typed as
   * `unknown` here so callers can hand-roll their own schemas without
   * pulling in a heavy schema-dts dep). Order matters for how Google
   * displays rich results — most specific first.
   */
  jsonLd?: unknown[];
};

// PNG, not the SVG that used to live here: LinkedIn, Facebook and X do not
// render SVG og:image at all, so every page sharing through this component was
// posting with no preview card. Caught while moving the content pages onto
// <SEO /> — they previously hard-coded /og/default.png and would have silently
// regressed.
const OG_IMAGE = `${BRANDING.url}/og/default.png`;

/**
 * Site-wide Organization JSON-LD. Identifies Apply Edge as a brand entity
 * to disambiguate from "Resume Edge" / "Career Edge" in the SERP
 * (docs/SEO_AUDIT.md). Keep `sameAs` accurate — every URL listed here is
 * a public Google-indexable signal that ties this domain to that profile.
 *
 * If you launch a Twitter/LinkedIn/etc. brand profile, add it here.
 */
const ORGANIZATION_LD = buildOrganizationJsonLd();

/** Site identity only; Google retired the sitelinks search box in 2024. */
const WEBSITE_LD = buildWebsiteJsonLd();

/**
 * SEO — every public page should render this. Emits canonical, OG/Twitter,
 * and JSON-LD (global Organization + WebSite, plus any per-page blocks via the
 * `jsonLd` prop).
 *
 * @example
 *   <SEO
 *     title={`Pricing — ${BRANDING.name}`}
 *     description="…"
 *     canonical="/pricing"
 *     jsonLd={[breadcrumbList, faqPage]}
 *   />
 */
export const SEO = ({ title, description, canonical, noindex, lang, jsonLd }: Props) => {
  const canonicalUrl = canonical ? `${BRANDING.url}${canonical}` : undefined;

  useLayoutEffect(() => {
    const managedTags = document.head.querySelectorAll<HTMLMetaElement>('meta[data-rh="true"]');
    let managedIndex = 0;
    while (managedIndex < managedTags.length) {
      const managed = managedTags[managedIndex];
      const key = managed.getAttribute("name") ? "name" : "property";
      const value = managed.getAttribute(key);
      if (value) {
        const tags = document.head.querySelectorAll<HTMLMetaElement>(`meta[${key}]`);
        let tagIndex = 0;
        while (tagIndex < tags.length) {
          const tag = tags[tagIndex];
          if (tag !== managed && tag.dataset.rh !== "true" && tag.getAttribute(key) === value) {
            tag.remove();
          }
          tagIndex += 1;
        }
      }
      managedIndex += 1;
    }
  }, []);

  return (
    // defer={false} is load-bearing, not a preference.
    //
    // react-helmet-async defaults to defer:true, which applies head mutations
    // inside a requestAnimationFrame callback. tools/scripts/ssg-snapshot.mjs
    // renders 4 routes CONCURRENTLY in headless Chromium, where rAF is
    // throttled (or never fires) for pages that aren't actively compositing.
    // React commits the <body> synchronously, so the snapshot looked fine —
    // but the <head> write was silently dropped, and every affected route
    // shipped the SPA shell's homepage <title>, no canonical, and no JSON-LD.
    // Observed live 2026-08-15: /pricing, /templates and /help all served
    // "AI Resume Builder — … | Apply Edge" with 0 data-rh tags, while /about
    // (identical code, same commit) won the race and was correct.
    //
    // With defer={false} the head is written during the commit phase, so the
    // snapshot always observes it. Cost is one synchronous DOM write per
    // navigation — negligible, and it makes client-side title updates land a
    // frame earlier too.
    <Helmet prioritizeSeoTags defer={false}>
      {lang && <html lang={lang} />}
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {noindex && <meta name="robots" content="noindex,follow" />}

      <meta property="og:type" content="website" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:site_name" content={BRANDING.name} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={OG_IMAGE} />

      {/* Global structured data — Organization + WebSite. */}
      <script type="application/ld+json">{JSON.stringify(ORGANIZATION_LD)}</script>
      <script type="application/ld+json">{JSON.stringify(WEBSITE_LD)}</script>
      {canonical === "/" && (
        <script type="application/ld+json">{JSON.stringify(buildSoftwareApplicationJsonLd("/"))}</script>
      )}

      {/* Per-page structured data (BreadcrumbList, FAQPage, etc.). */}
      {jsonLd?.map((block, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <script key={index} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};
